/**
 * Multi-selection state for grouping.
 *
 * Single-element selection remains what it has always been: a `.selected` class that
 * use-resizer manages imperatively. This hook does not replace it or interfere with
 * it — it only listens for the clear event so that clicking a lone element also drops
 * any group selection, keeping the two systems from showing selection at once.
 *
 * The state itself lives in groupSelectionStore so the overlay and the context menu,
 * which sit in different branches of the tree, act on the same selection.
 */

import { useCallback, useEffect, useState } from 'react';
import { SLIDE_SELECTION_CLEAR } from '../../hooks/use-resizer';
import {
  getGroupSelection, subscribeGroupSelection, setGroupSelection,
} from './groupSelectionStore';

export const useGroupSelection = () => {
  const [snapshot, setSnapshot] = useState(getGroupSelection);

  useEffect(() => {
    const sync = () => setSnapshot(getGroupSelection());
    // Re-read on mount as well: the store may have changed between the initial
    // useState call and the subscription landing.
    sync();
    return subscribeGroupSelection(sync);
  }, []);

  const clear = useCallback(() => {
    setGroupSelection((prev) => (
      prev.selectedIds.length === 0 && prev.activeGroupId === null
        ? prev
        : { selectedIds: [], activeGroupId: null }
    ));
  }, []);

  const select = useCallback((ids) => {
    setGroupSelection({ selectedIds: ids, activeGroupId: null });
  }, []);

  /**
   * Add `id` to the selection, or drop it if it is already there.
   *
   * `seedId` folds in an object that was selected the ordinary way before the first
   * modifier-click. That selection lives only as a DOM class on the element, so
   * without seeding it, Ctrl+clicking a second object would start from an empty list
   * and silently forget the first.
   */
  const toggle = useCallback((id, seedId = null) => {
    setGroupSelection((prev) => {
      const base = seedId && !prev.selectedIds.includes(seedId)
        ? [...prev.selectedIds, seedId]
        : prev.selectedIds;
      return {
        activeGroupId: null,
        selectedIds: base.includes(id) ? base.filter((x) => x !== id) : [...base, id],
      };
    });
  }, []);

  const selectGroup = useCallback((groupId, memberIds) => {
    setGroupSelection({ selectedIds: memberIds, activeGroupId: groupId });
  }, []);

  // use-resizer fires this whenever a single element takes selection.
  useEffect(() => {
    window.addEventListener(SLIDE_SELECTION_CLEAR, clear);
    return () => window.removeEventListener(SLIDE_SELECTION_CLEAR, clear);
  }, [clear]);

  return {
    selectedIds: snapshot.selectedIds,
    activeGroupId: snapshot.activeGroupId,
    toggle,
    select,
    selectGroup,
    clear,
  };
};

export default useGroupSelection;
